import React from "react";
import { Alert, Modal, Space, Table, Tag, Typography } from "antd";
import { CONTENT_HEALTH_STATUS_TAGS } from "../../../constants/status-colors";
import {
  GameQuizHomeworkRef,
  GameQuizResourceRef,
  GameQuizUsage,
} from "../../../services/gameQuiz/gameQuiz.types";

const { Paragraph, Text, Title } = Typography;

interface DeleteGameQuizModalProps {
  open: boolean;
  /** Null while the impact is still being fetched. */
  usage: GameQuizUsage | null;
  loading: boolean;
  deleting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const resourceColumns = [
  { title: "ID", dataIndex: "id", key: "id", width: 90 },
  { title: "Recurso", dataIndex: "title", key: "title" },
  {
    title: "Tipo",
    dataIndex: "type",
    key: "type",
    width: 140,
    render: (type: string | null) => type ?? "—",
  },
];

const homeworkColumns = [
  { title: "ID", dataIndex: "id", key: "id", width: 90 },
  { title: "Tarea", dataIndex: "title", key: "title" },
  {
    title: "Salón",
    dataIndex: "classroom_id",
    key: "classroom_id",
    width: 100,
    render: (id: number | null) => id ?? "—",
  },
];

/**
 * Impact dialog shown before deleting a Quiz Emmi.
 *
 * It exists because a bare delete used to leave a Resource pointing at the
 * deleted quiz; a teacher could then assign that broken Resource as homework and
 * the student hit an inescapable 404 when opening the challenge. The admin now
 * sees exactly what will be removed and what will merely be unlinked, and has to
 * say so explicitly.
 */
const DeleteGameQuizModal: React.FC<DeleteGameQuizModalProps> = ({
  open,
  usage,
  loading,
  deleting,
  onCancel,
  onConfirm,
}) => {
  const hasConnections = usage?.hasConnections ?? false;

  return (
    <Modal
      open={open}
      title={
        usage ? `Eliminar "${usage.title}"` : "Eliminar quiz"
      }
      okText={hasConnections ? "Eliminar y desconectar" : "Eliminar"}
      cancelText="Cancelar"
      okButtonProps={{ danger: true, loading: deleting, disabled: loading }}
      cancelButtonProps={{ disabled: deleting }}
      onOk={onConfirm}
      onCancel={onCancel}
      confirmLoading={deleting}
      width={720}
      destroyOnClose
    >
      {loading || !usage ? (
        <Paragraph>Revisando qué depende de este quiz…</Paragraph>
      ) : !hasConnections ? (
        <Paragraph>
          Este quiz no está conectado a ningún recurso ni tarea. Se puede
          eliminar sin afectar nada más.
        </Paragraph>
      ) : (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Alert
            type="warning"
            showIcon
            message="Este quiz está en uso"
            description="Si lo eliminás sin desconectar lo que depende de él, la app les muestra a los alumnos un error del que no pueden salir. Por eso la eliminación limpia las conexiones."
          />

          {usage.dedicatedResources.length > 0 && (
            <div>
              <Title level={5}>
                Se eliminan también{" "}
                <Tag color={CONTENT_HEALTH_STATUS_TAGS.deleted.color}>
                  {usage.dedicatedResources.length} recurso(s)
                </Tag>
              </Title>
              <Paragraph type="secondary" style={{ marginBottom: 8 }}>
                Estos recursos existen solo para mostrar este quiz, así que sin
                él no abren nada.
              </Paragraph>
              <Table<GameQuizResourceRef>
                size="small"
                rowKey="id"
                pagination={false}
                columns={resourceColumns}
                dataSource={usage.dedicatedResources}
                locale={{ emptyText: "Sin recursos" }}
              />
            </div>
          )}

          {usage.homeworks.length > 0 && (
            <div>
              <Title level={5}>
                Se quitan{" "}
                <Tag color={CONTENT_HEALTH_STATUS_TAGS.deleted.color}>
                  {usage.homeworks.length} tarea(s) asignada(s)
                </Tag>
              </Title>
              <Paragraph type="secondary" style={{ marginBottom: 8 }}>
                Están asignadas sobre los recursos de arriba, así que dejarían de
                poder resolverse.
              </Paragraph>
              <Table<GameQuizHomeworkRef>
                size="small"
                rowKey="id"
                pagination={false}
                columns={homeworkColumns}
                dataSource={usage.homeworks}
                locale={{ emptyText: "Sin tareas" }}
              />
            </div>
          )}

          {usage.attachedResources.length > 0 && (
            <div>
              <Title level={5}>
                Se desvinculan{" "}
                <Tag color={CONTENT_HEALTH_STATUS_TAGS.detached.color}>
                  {usage.attachedResources.length} recurso(s)
                </Tag>
              </Title>
              <Paragraph type="secondary" style={{ marginBottom: 8 }}>
                Tenían este quiz asociado como extra opcional. Siguen
                funcionando; solo pierden el quiz.
              </Paragraph>
              <Table<GameQuizResourceRef>
                size="small"
                rowKey="id"
                pagination={false}
                columns={resourceColumns}
                dataSource={usage.attachedResources}
                locale={{ emptyText: "Sin recursos" }}
              />
            </div>
          )}

          {usage.attemptCount > 0 && (
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              <Text strong>{usage.attemptCount}</Text> intento(s) de alumnos se
              conservan como historial: no se borran.
            </Paragraph>
          )}
        </Space>
      )}
    </Modal>
  );
};

export default DeleteGameQuizModal;
